/**
 * Carrito temporal de Acceso General (Sprint 57 · F4B).
 *
 * Persistencia mínima en `sessionStorage`: SÓLO ids de salón + cantidades,
 * asociado al `eventoId` y al socio actual. NUNCA persiste precios como
 * autoridad; los importes se revalidan siempre contra `compra-contexto`.
 *
 * idempotencyKey del carrito lógico confirmado:
 *   - Se mantiene estable mientras la firma (evento + items ordenados) no
 *     cambie → doble clic / reintento de red reutilizan la MISMA clave.
 *   - Si el socio modifica cantidades, la firma cambia y se genera una clave
 *     nueva (una intención de compra distinta).
 */

const KEY = "ct_carrito_ag_v1";

const isBrowser = () =>
  typeof window !== "undefined" && typeof window.sessionStorage !== "undefined";

const read = () => {
  if (!isBrowser()) return null;
  try {
    const raw = window.sessionStorage.getItem(KEY);
    return raw ? JSON.parse(raw) : null;
  } catch {
    return null;
  }
};

const write = (v) => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.setItem(KEY, JSON.stringify(v));
  } catch {
    /* ignore */
  }
};

export const limpiarCarritoAG = () => {
  if (!isBrowser()) return;
  try {
    window.sessionStorage.removeItem(KEY);
  } catch {
    /* ignore */
  }
};

/** Firma determinista del carrito: evento + items (salonId:cantidad) ordenados. */
const firma = (eventoId, cantidades) => {
  const items = Object.entries(cantidades || {})
    .filter(([, n]) => Number(n) > 0)
    .map(([sid, n]) => `${sid}:${Number(n)}`)
    .sort();
  return `${eventoId}|${items.join(",")}`;
};

/**
 * Restaura las cantidades guardadas para un evento y socio.
 * Devuelve `{ [salonAccesoId]: cantidad }` o `{}` si no hay o no coincide.
 * Los datos incompatibles (otro evento u otro socio) se descartan.
 */
export const restaurarCantidadesAG = (eventoId, socioId) => {
  const c = read();
  if (!c || c.eventoId !== eventoId || c.socioId !== socioId) return {};
  return c.cantidades && typeof c.cantidades === "object" ? c.cantidades : {};
};

/** Guarda las cantidades elegidas (sin precios) para navegación interna. */
export const guardarCantidadesAG = (eventoId, socioId, cantidades) => {
  const prev = read();
  const key =
    prev && prev.eventoId === eventoId && prev.socioId === socioId
      ? prev.idemKey
      : null;
  write({
    eventoId,
    socioId,
    cantidades: cantidades || {},
    idemKey: key || null,
    idemFirma: prev ? prev.idemFirma : null,
  });
};

const nuevaKey = () =>
  `CMP-AG-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`.toUpperCase();

/**
 * Devuelve la idempotencyKey para el carrito confirmado. Reutiliza la clave
 * previa si la firma no cambió; genera una nueva si cambió (o no existía).
 */
export const idempotencyKeyCarritoAG = (eventoId, socioId, cantidades) => {
  const f = firma(eventoId, cantidades);
  const c = read() || { eventoId, socioId, cantidades };
  if (c.eventoId === eventoId && c.socioId === socioId && c.idemFirma === f && c.idemKey) {
    return c.idemKey;
  }
  const key = nuevaKey();
  write({ eventoId, socioId, cantidades, idemKey: key, idemFirma: f });
  return key;
};
